import type { CSSProperties } from "react";

import { flatElementStylesToReactCss } from "../runtime/flatElementStyles";

/** Box model on the summary row; typography is applied on the inner heading. */
const FAQ_SUMMARY_BOX_KEYS = [
	"padding",
	"paddingTop",
	"paddingRight",
	"paddingBottom",
	"paddingLeft",
	"margin",
	"marginTop",
	"marginRight",
	"marginBottom",
	"marginLeft",
	"width",
	"minWidth",
	"maxWidth",
	"height",
	"minHeight",
	"maxHeight",
	"background",
	"backgroundColor",
	"border",
	"borderWidth",
	"borderStyle",
	"borderColor",
	"borderRadius",
	"borderTop",
	"borderRight",
	"borderBottom",
	"borderLeft",
] as const satisfies readonly (keyof CSSProperties)[];

function pickSummaryBoxStyles(questionCss: CSSProperties): CSSProperties {
	const out: CSSProperties = {};
	for (const key of FAQ_SUMMARY_BOX_KEYS) {
		const v = questionCss[key];
		if (v !== undefined && v !== "") {
			(out as Record<string, unknown>)[key] = v;
		}
	}
	return out;
}

function hasGradientTextFill(questionCss: CSSProperties): boolean {
	return (
		typeof questionCss.WebkitTextFillColor === "string" ||
		typeof questionCss.backgroundClip === "string" ||
		typeof questionCss.WebkitBackgroundClip === "string"
	);
}

/**
 * FAQ questions render inside `<summary>` as a semantic heading (`h3` by default).
 * Apply only *explicit* question typography inline so Theme Manager tag rules
 * can cascade when factory defaults omit size/weight/color overrides.
 * Author font sizes also ship as scoped `!important` CSS via `faqDesign.ts`.
 */
export function faqQuestionRowStyles(questionFlat: Record<string, unknown>): {
	summaryRow: CSSProperties;
	headingStyle: CSSProperties;
} {
	const questionCss = flatElementStylesToReactCss(questionFlat);
	const summaryBox = pickSummaryBoxStyles(questionCss);

	const headingStyle: CSSProperties = {
		flex: 1,
		minWidth: 0,
		margin: 0,
	};

	const typographyKeys = [
		"color",
		"fontSize",
		"fontWeight",
		"fontFamily",
		"lineHeight",
		"letterSpacing",
		"textAlign",
		"textTransform",
		"textDecoration",
		"WebkitTextFillColor",
		"backgroundClip",
		"WebkitBackgroundClip",
		"backgroundImage",
		"opacity",
	] as const satisfies readonly (keyof CSSProperties)[];

	for (const key of typographyKeys) {
		const v = questionCss[key];
		if (v !== undefined && v !== "") {
			(headingStyle as Record<string, unknown>)[key] = v;
		}
	}

	// Only fall back to theme fg when an author set a solid color path that
	// cleared to empty while gradient is absent — leave unset so `h*` CSS wins.
	if (
		headingStyle.color === undefined &&
		!hasGradientTextFill(questionCss) &&
		typeof questionFlat.color === "string" &&
		questionFlat.color.trim() === ""
	) {
		headingStyle.color = "var(--tt-color-fg)";
	}

	const summaryRow: CSSProperties = {
		display: "flex",
		alignItems: "center",
		gap: "0.5rem",
		listStyle: "none",
		cursor: "pointer",
		width: "100%",
		boxSizing: "border-box",
		...summaryBox,
	};

	return { summaryRow, headingStyle };
}
